<script setup lang="ts">
import type { AddressForm, CustomerDto } from '@/types';
import { onMounted, ref } from 'vue';
import { onBeforeRouteLeave } from 'vue-router';
import local from '@/stores/local';
import BottomBarComponent from '@/components/BottomBarComponent.vue';
import BackComponent from '@/components/BackComponent.vue';
import NextComponent from '@/components/NextComponent.vue';
import ContactInfoComponent from '@/components/ContactInfoComponent.vue';
import AddressInfoComponent from '@/components/AddressInfoComponent.vue';
import { useCustomerStore } from '@/stores/customer';
import { storeToRefs } from 'pinia';
import { useAgreementStore } from '@/stores/agreement';
import { useConfigStore } from '@/stores/config';
import { useIframeStore } from '@/stores/iframe';

const customerStore = useCustomerStore()
const { agreement } = storeToRefs(useAgreementStore())
const { config } = storeToRefs(useConfigStore())
const iframeStore = useIframeStore()

const valid = ref(true)
const customerForm = ref<Partial<CustomerDto>>({})
const addressForm = ref<AddressForm>({})

function onCustomerFound(customerId: number) {
  agreement.value.customerId = customerId
}

function onCustomerNotFound() {
  agreement.value.customerId = undefined
}

onBeforeRouteLeave(() => {
  customerStore.savedCustomer = customerForm.value as CustomerDto
  local.setItem('customerForm', JSON.parse(JSON.stringify(customerForm.value)))
  local.setItem('addressForm', JSON.parse(JSON.stringify(addressForm.value)))
})

onMounted(async () => {
  if (iframeStore.isIframeMode && iframeStore.agreementResponsible) {
    agreement.value.agreementresponsible = iframeStore.agreementResponsible
  }

  const savedCustomerForm = await local.getItem<Partial<CustomerDto>>('customerForm')
  const savedAddressForm = await local.getItem<AddressForm>('addressForm')

  if (savedCustomerForm) {
    customerForm.value = savedCustomerForm
  }

  if (savedAddressForm) {
    addressForm.value = savedAddressForm
  }
})
</script>

<template>
  <v-container>
    <v-form v-model="valid">
      <v-row>
        <v-col>
          <ContactInfoComponent
            v-model="customerForm"
            @found="onCustomerFound"
            @not-found="onCustomerNotFound"
          />
        </v-col>
      </v-row>

      <v-row>
        <v-col>
          <AddressInfoComponent
            v-model:customer-form="customerForm"
            v-model:address-form="addressForm"
          />
        </v-col>
      </v-row>

      <v-row v-if="config?.options?.length && !iframeStore.isIframeMode">
        <v-col>
          <v-select
            v-model="agreement.agreementresponsible"
            :items="config?.options"
            :label="config?.optionsLabel ?? 'Vælg venligst'"
            :disabled="false"
          />
        </v-col>
      </v-row>
    </v-form>
  </v-container>

  <BottomBarComponent>
    <BackComponent to="/agreement/addons" />
    <NextComponent
      to="/agreement/confirm"
      :disabled="!valid || !addressForm.selected || (!iframeStore.isIframeMode && !!config?.options?.length && !agreement.agreementresponsible)"
    />
  </BottomBarComponent>
</template>
